/**
 * Escala de apilamiento de las capas flotantes de la app.
 *
 * Por qué existe: cuatro capas vivían todas en z-index 9000 (banner de suspensión
 * parcial, PushBlockedModal, VerifyEmailBanner y el asistente). Con el mismo valor
 * decide el orden del DOM, y ganaba siempre el asistente: el modal de push quedaba
 * inalcanzable detrás, y el usuario con suspensión parcial no podía llegar al banner
 * de apelación, que es justo lo que necesita para desbloquearse.
 *
 * El orden es intencional, de menos a más urgente:
 *   optionalPrompt   aviso OPCIONAL (PushPrompt). Cede ante todo. Ver el recuadro.
 *   actionBar        barra fija de acción de una pantalla (comprar, siguiente,
 *                    enviar). Es donde el usuario DECIDE.
 *   verifyBanner     aviso discreto, cede ante todo lo de arriba
 *   assistant        panel del asistente (se conserva en 9000: es el valor que ya
 *                    tenía, y AssistantPanel.js —de otro ejecutor— sigue en 9000)
 *   suspensionBanner la apelación no puede quedar tapada por nada de lo anterior
 *   blockingModal    modal que exige una acción que el usuario acaba de pedir
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ 🔴 UN AVISO OPCIONAL NUNCA POR ENCIMA DE UN CONTROL DE DECISIÓN          ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * `PushPrompt` vivía suelto en 8000 —fuera de esta escala— y es `fixed` a 84px
 * del fondo, exactamente la franja donde varias pantallas ponen su barra de
 * acción. Medido con `elementFromPoint` en /checkout a 390×844: el toque sobre
 * «Siguiente» (y 686→733) lo recibía el botón «Sí, activar» del aviso, y el de
 * «Capturar ubicación de entrega» lo recibía el PÁRRAFO del aviso. Le pasa al
 * 100% de las cuentas nuevas —el aviso sale por defecto— en su primera compra.
 *
 * El arreglo NO fue subir las barras: subirlas al rango de los banners las
 * habría puesto por encima de los modales bloqueantes (2000) y de las pantallas
 * que exigen una acción. Se bajó el aviso, que es el que sobra en esa disputa:
 *
 *   optionalPrompt (40) < actionBar (100)
 *
 * `actionBar` vale 100 a propósito: es el número que Checkout y ProductDetail ya
 * tenían. Nombrarlo no mueve un píxel; lo que aporta es que el invariante
 * («ninguna barra de acción por debajo del aviso») queda comprobable, y que la
 * próxima barra fija nace con la capa correcta en vez de con un número suelto.
 *
 * ⚠️ EL ORDEN NO BASTA SOLO, y conviene saber por qué: en ESCRITORIO el
 * `<main style={{ position:'relative', zIndex:1 }}>` de App.js abre un CONTEXTO
 * DE APILAMIENTO (es el hallazgo F-4, con su prueba en `App.stacking.test.js`),
 * así que la barra de una página queda encerrada bajo z=1 pida lo que pida,
 * mientras el aviso —que es global y vive fuera de `main`— compite a nivel
 * raíz. Entre hermanos el orden manda; entre contextos distintos, no.
 *
 * Por eso la PRIMERA defensa es que el aviso solo se pinta en rutas sin barra
 * inferior propia (lista BLANCA en PushPrompt.js, mismo patrón que
 * VerifyEmailBanner), y esta escala es la segunda: cubre el móvil, cubre a los
 * hermanos y deja el invariante escrito para la barra que se cree mañana.
 *
 * Fuera de esta escala y sin tocar: el nav colapsado (1600) y su menú abierto
 * (9100/9050), Toast (10000), EmailCodeModal (12000), TwoFactorGate (12050),
 * LaunchGate (99998) y suspensión total (99999).
 */
export const zLayer = {
  optionalPrompt: 40,
  actionBar: 100,
  verifyBanner: 8900,
  assistant: 9000,
  suspensionBanner: 9200,
  blockingModal: 9300,
};
